import {
  CheckboxField,
  ChevronRightIcon,
  Modal,
  SearchField,
  Typography /** Media query result is used within a client-only modal; this is safe to use. */,
  // eslint-disable-next-line no-restricted-imports
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { useNavigation } from 'react-router';

import {
  useProductSearchFilters,
  useSearchParamFilter,
} from '@jane/bloom/hooks/filters';
import { normalizeString } from '@jane/bloom/utils/strings/normalizeString';
import type { ProductSearchFilter } from '@jane/bloom/viewModels/productSearchFilters';

import { DeselectAll } from './deselectAll';

import styles from './modalFiltersSection.module.css';

interface ModalBrandsFilterEntrypointProps {
  filterKey: 'brands' | 'terpenes';
  handleClick: () => void;
}

export function ModalFilterEntrypoint({
  filterKey,
  handleClick,
}: ModalBrandsFilterEntrypointProps) {
  const isMobile = useMobileMediaQuery({});
  return (
    <>
      <div
        className={clsx(styles['entrypoint'], 'focus-visible')}
        tabIndex={0}
        role="button"
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleClick();
          }
        }}
      >
        <Typography
          variant={isMobile ? 'body' : 'header-bold'}
          className={styles['entrypoint__title']}
        >
          {filterKey === 'brands' ? 'Brands' : 'Top terpenes'}
        </Typography>
        <ChevronRightIcon color="text-main" />
      </div>
      <div className={styles['entrypoint__divider']} />
    </>
  );
}

interface ModalFilterSectionProps {
  resetting?: boolean;
  selectedTab: 'brands' | 'terpenes';
}

export function ModalFilterSection({
  resetting,
  selectedTab,
}: ModalFilterSectionProps) {
  const { clearFilter, getFilterOptionValue, getFilterStatus, setFilter } =
    useSearchParamFilter(selectedTab);
  const [deselecting, setDeselecting] = useState(false);
  const { state } = useNavigation();
  const [search, setSearch] = useState('');
  const { getProductSearchFilterOptions } = useProductSearchFilters();

  const filterOptions = getProductSearchFilterOptions(selectedTab);

  const listItems = (filterOptions as ProductSearchFilter).filter(
    (searchItem) =>
      normalizeString(searchItem.value.toLowerCase()).includes(
        normalizeString(search.toLowerCase())
      )
  );
  const alphabeticallyOrderedItems = listItems.sort((a, b) =>
    a.value.toLocaleLowerCase() > b.value.toLocaleLowerCase() ? 1 : -1
  );
  const deselectAllBrands = () => {
    setDeselecting(true);
    clearFilter();
  };

  useEffect(() => {
    if (deselecting && state === 'idle') {
      setDeselecting(false);
    }
  }, [deselecting, state]);

  return (
    <>
      <Modal.Header
        title={selectedTab === 'brands' ? 'Brands' : 'Top terpenes'}
        branded
        actions={<DeselectAll onClick={deselectAllBrands} />}
        nested
      >
        <SearchField
          disableMobileInputStyling
          borderRadius="lg"
          label={
            selectedTab === 'brands' ? 'Search for brand' : 'Search for terpene'
          }
          name={selectedTab === 'brands' ? 'brands-search' : 'terpenes-search'}
          onChange={setSearch}
          width="100%"
          mt={24}
        />
      </Modal.Header>
      <Modal.Content>
        {alphabeticallyOrderedItems.map((item) => {
          return (
            <Fragment key={item.value}>
              <CheckboxField
                className={styles['checkbox']}
                label={`${item.label} (${item.count})`}
                name={item.value}
                defaultChecked={getFilterStatus(item.value) === 'selected'}
                checked={resetting || deselecting ? false : undefined}
                disabled={item.count === 0}
                onChange={(val) => {
                  if (val !== !!getFilterOptionValue(item.value)) {
                    setFilter(item.value, val);
                  }
                }}
              />
              <Modal.ContentDivider color="grays-ultralight" height={2} />
            </Fragment>
          );
        })}
      </Modal.Content>
    </>
  );
}
